import { useUserStore } from '@/stores/userStore';
import type { RouteRecordRaw } from 'vue-router';
import { accessTasks, accessTransactions } from '@/permissions/exchange';
import { isComplianceOfficer } from '@/permissions/roles';
import { complianceUserWithLocalExchangeEnabled } from '@/permissions/compliance';

const routes: RouteRecordRaw[] = [
	{
		path: '/compliance',
		beforeEnter: [canAccessComplianceRoutes],
		children: [
			{
				path: 'dashboard',
				name: 'ComplianceDashboard',
				component: () => import('@/views/ComplianceDashboard.vue'),
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageComplianceDashboard.metaTitle',
					permissionRule: isComplianceOfficer,
				},
			},
			{
				path: 'transactions',
				name: 'ComplianceTransactionsParent',
				component: () => import('@/views/ExchangeTransactionsParent.vue'),
				beforeEnter: [canAccessTransactions],
				children: [
					{
						path: '',
						name: 'ComplianceTransactions',
						component: () => import('@/views/TransactionsIndex.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeTransactions.metaTitle',
							permissionRule: accessTransactions,
							profileSwitchRedirect: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.isComplianceOfficer
									? 'ComplianceTransactions'
									: 'ExchangeTransactions';
							},
						},
					},
					{
						path: 'agreements',
						name: 'ComplianceAgreements',
						component: () => import('@/views/AgreementsIndex.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeAgreements.metaTitle',
							permissionRule: accessTransactions,
							profileSwitchRedirect: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.isComplianceOfficer
									? 'ComplianceAgreements'
									: 'ExchangeAgreements';
							},
						},
					},
					{
						path: 'tasks',
						name: 'ComplianceTasks',
						component: () => import('@/views/TasksIndex.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeTasks.metaTitle',
							permissionRule: accessTasks,
							profileSwitchRedirect: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.isComplianceOfficer
									? 'ComplianceTasks'
									: 'ExchangeTasks';
							},
						},
					},
				],
			},
			{
				path: 'transactions/:transactionUuid',
				name: 'ComplianceTransaction',
				component: () => import('@/views/ExchangeTransaction.vue'),
				props: true,
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageTransaction.metaTitle',
					permissionRule: accessTransactions,
					profileSwitchRedirect: 'ComplianceTransactions',
				},
				beforeEnter: [canAccessTransactions],
			},
			{
				path: 'agreements',
				redirect: { name: 'ComplianceAgreements' },
			},
			{
				path: 'agreements/:agreementUuid',
				name: 'ComplianceAgreement',
				component: () => import('@/views/ExchangeAgreement.vue'),
				props: true,
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageAgreement.metaTitle',
					permissionRule: accessTransactions,
				},
				beforeEnter: [canAccessTransactions],
			},
			{
				path: 'settings',
				name: 'ComplianceSettingsParent',
				component: () => import('@/views/ComplianceSettingsParent.vue'),
				redirect: { name: 'ComplianceSettingsSchool' },
				children: [
					{
						path: 'school',
						name: 'ComplianceSettingsSchool',
						component: () => import('@/views/ComplianceSettingsSchool.vue'),
						meta: {
							layout: 'LayoutAuthenticated',
							requiresAuth: true,
							title: 'pageComplianceSettingsSchool.metaTitle',
							permissionRule: isComplianceOfficer,
						},
					},
					{
						path: 'local-exchange',
						name: 'ComplianceSettingsExchange',
						component: () => import('@/views/ComplianceSettingsExchange.vue'),
						meta: {
							layout: 'LayoutAuthenticated',
							requiresAuth: true,
							title: 'pageComplianceSettingsExchange.metaTitle',
							permissionRule: complianceUserWithLocalExchangeEnabled,
						},
					},
					{
						path: 'users',
						name: 'ComplianceSettingsUsers',
						component: () => import('@/views/ComplianceSettingsUsers.vue'),
						meta: {
							layout: 'LayoutAuthenticated',
							requiresAuth: true,
							title: 'pageComplianceSettingsUsers.metaTitle',
							permissionRule: isComplianceOfficer,
						},
					},
					{
						path: 'athlete-exports',
						name: 'ComplianceSettingsAthleteExports',
						component: () => import('@/views/ComplianceSettingsAthleteExports.vue'),
						meta: {
							layout: 'LayoutAuthenticated',
							requiresAuth: true,
							title: 'pageComplianceSettingsAthleteExports.metaTitle',
							permissionRule: isComplianceOfficer,
						},
					},
				],
			},
			{
				path: 'brands',
				name: 'ComplianceBrands',
				component: () => import('@/views/ComplianceBrands.vue'),
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageComplianceBrands.metaTitle',
					permissionRule: complianceUserWithLocalExchangeEnabled,
				},
			},
			{
				path: 'agents',
				name: 'ComplianceAgents',
				component: () => import('@/views/ComplianceAgents.vue'),
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageComplianceAgents.metaTitle',
					permissionRule: isComplianceOfficer,
				},
			},
		],
	},
];

function canAccessComplianceRoutes() {
	const userStore = useUserStore();
	if (!userStore.profileAccess.isComplianceOfficer) {
		return { name: 'NotFound' };
	}
}

function canAccessTransactions() {
	const userStore = useUserStore();
	if (!userStore.profileAccess.canAccessTransactions) {
		return { name: 'NotFound' };
	}
}

export default routes;
